import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../api';
import {
  GetOnboardingSuggestionsResponse,
  CompleteSuggestionsResponse,
  OnboardingSuggestion,
  AnalyzeTodoResponse,
  UserTodoItem,
} from '@dreamai/types';
import { OnboardingStepProps } from '../OnboardingFlow';
import { useOrganisationsEnabled } from '../../../hooks/useOrganisationsEnabled';
import AddTodoModal from '../../chat/sidebar/AddTodoModal';
import { OnboardingModalsProvider } from '../../../providers/OnboardingModalsProvider';
import { ReactComponent as OnboardingTaskIcon } from '../../../icons/onboarding-task.svg';
import { ReactComponent as OnboardingProjectIcon } from '../../../icons/onboarding-project.svg';
import { ReactComponent as OnboardingAutopilotIcon } from '../../../icons/onboarding-autopilot.svg';
import './suggestions-step.scss';

// ── Inline SVG icons for action buttons ─────────────────────────────────

const PlusIcon: FC = () => (
  <svg width='14' height='14' viewBox='0 0 14 14' fill='none'>
    <path
      d='M7 1v12M1 7h12'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
    />
  </svg>
);

const CheckIcon: FC = () => (
  <svg width='12' height='12' viewBox='0 0 12 12' fill='none'>
    <path
      d='M2 6L5 9L10 3'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

// ── Types ───────────────────────────────────────────────────────────────

export type OnboardingDestination = 'home' | 'invite-colleagues';

interface SuggestionsStepProps extends OnboardingStepProps {
  assistantId: string | null;
  assistantName: string | null;
  assistantNickname: string | null;
  assistantImageUrl: string | null;
  assistantPredominantColour: string | null;
  /** Where to land once onboarding is complete. */
  onFinished: (destination: OnboardingDestination) => Promise<void>;
}

type SuggestionType = 'task' | 'project' | 'autopilot';

interface DisplaySuggestion extends OnboardingSuggestion {
  originalIndex: number;
}

// ── Constants ───────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 3000;
const DISPLAY_COUNT = 6;

/** Action keys excluded during onboarding — no buying, no "project" (handled separately) */
const ONBOARDING_EXCLUDE_ACTION_KEYS = [
  'project',
  'buy',
  'bring_and_buy',
  'buy_and_act',
];

const TYPE_CONFIG: Record<
  SuggestionType,
  {
    label: string;
    tagClass: string;
    subtitle: string;
  }
> = {
  task: {
    label: 'TASKS',
    tagClass: 'type-tag-task',
    subtitle: "I'll get working on this",
  },
  project: {
    label: 'PROJECTS',
    tagClass: 'type-tag-project',
    subtitle: "Ready. I'll discuss this with you to start planning",
  },
  autopilot: {
    label: 'AUTOPILOTS',
    tagClass: 'type-tag-autopilot',
    subtitle: "Ready. I'll set this up to run on autopilot",
  },
};

// ── Selection logic: pick 6 suggestions to display ──────────────────────

function selectDisplaySuggestions(
  suggestions: OnboardingSuggestion[],
): DisplaySuggestion[] {
  const indexed = suggestions.map((s, i) => ({ ...s, originalIndex: i }));

  // Find highest-ranked (lowest index) of each type
  const firstTask = indexed.find((s) => s.type === 'task');
  const firstProject = indexed.find((s) => s.type === 'project');
  const firstAutopilot = indexed.find((s) => s.type === 'autopilot');

  const guaranteed: DisplaySuggestion[] = [];
  const usedIndices = new Set<number>();

  for (const item of [firstTask, firstProject, firstAutopilot]) {
    if (item) {
      guaranteed.push(item);
      usedIndices.add(item.originalIndex);
    }
  }

  // Fill remaining slots with next highest ranked
  const remaining = indexed.filter((s) => !usedIndices.has(s.originalIndex));
  const needed = DISPLAY_COUNT - guaranteed.length;
  const fillers = remaining.slice(0, needed);

  return [...guaranteed, ...fillers];
}

// ── Component ───────────────────────────────────────────────────────────

const SuggestionsStep: FC<SuggestionsStepProps> = ({
  onFinished,
  assistantId,
  assistantName,
  assistantNickname,
  assistantImageUrl,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const organisationsEnabled = useOrganisationsEnabled();

  const [suggestions, setSuggestions] = useState<OnboardingSuggestion[]>([]);
  const [generationStatus, setGenerationStatus] = useState<
    'pending' | 'completed' | 'failed'
  >('pending');
  const [actionedIndices, setActionedIndices] = useState<Set<number>>(
    new Set(),
  );
  const [actioning, setActioning] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // AddTodoModal state
  const [todoModalOpen, setTodoModalOpen] = useState(false);
  const [todoModalSuggestion, setTodoModalSuggestion] =
    useState<DisplaySuggestion | null>(null);

  // Pre-loaded analysis cache: originalIndex → AnalyzeTodoResponse
  const analysisCache = useRef<Map<number, AnalyzeTodoResponse>>(new Map());

  // Overridden descriptions: originalIndex → user-edited text (when user changes the text in the modal)
  const [overriddenDescriptions, setOverriddenDescriptions] = useState<
    Map<number, string>
  >(new Map());

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevStatusRef = useRef<'pending' | 'completed' | 'failed'>('pending');

  // ── Fetch suggestions ───────────────────────────────────────────────

  const fetchSuggestions = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const { data } = await api.get<GetOnboardingSuggestionsResponse>(
        '/onboarding/suggestions',
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (data.success) {
        prevStatusRef.current = data.status;
        setSuggestions(data.suggestions);
        setGenerationStatus(data.status);

        // Restore actioned state from backend
        if (data.actionedIndices && data.actionedIndices.length > 0) {
          setActionedIndices(new Set(data.actionedIndices));
        }

        // Restore description overrides from backend
        if (data.descriptionOverrides) {
          setOverriddenDescriptions((prev) => {
            const next = new Map(prev);
            for (const [idx, desc] of Object.entries(
              data.descriptionOverrides!,
            )) {
              next.set(Number(idx), desc);
            }
            return next;
          });
        }

        // Stop polling once generation is done
        if (data.status !== 'pending' && pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }
    } catch (err) {
      console.error('[Suggestions] Failed to fetch suggestions:', err);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchSuggestions();
    pollRef.current = setInterval(fetchSuggestions, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
    };
  }, [fetchSuggestions]);

  // ── Compute display suggestions ─────────────────────────────────────

  const displaySuggestions = useMemo(
    () => selectDisplaySuggestions(suggestions),
    [suggestions],
  );

  // Group by type for rendering
  const groupedSuggestions = useMemo(() => {
    const groups: Record<SuggestionType, DisplaySuggestion[]> = {
      task: [],
      project: [],
      autopilot: [],
    };
    for (const s of displaySuggestions) {
      const type = s.type || 'task';
      groups[type].push(s);
    }
    return groups;
  }, [displaySuggestions]);

  // ── Pre-load analysis for task suggestions ─────────────────────────

  useEffect(() => {
    const taskSuggestions = displaySuggestions.filter(
      (s) => (s.type || 'task') === 'task',
    );
    if (taskSuggestions.length === 0) return;

    let cancelled = false;

    (async () => {
      try {
        const token = await getAccessTokenSilently();
        await Promise.all(
          taskSuggestions.map(async (s) => {
            if (analysisCache.current.has(s.originalIndex)) return;
            try {
              const { data } = await api.post<AnalyzeTodoResponse>(
                '/user-todos/analyze',
                {
                  description: s.shortDescription,
                  excludeActionKeys: ONBOARDING_EXCLUDE_ACTION_KEYS,
                },
                { headers: { Authorization: `Bearer ${token}` } },
              );
              if (!cancelled && data.success) {
                analysisCache.current.set(s.originalIndex, data);
              }
            } catch (err) {
              console.warn(
                `[Suggestions] Pre-load analysis failed for "${s.shortDescription}":`,
                err,
              );
            }
          }),
        );
      } catch (err) {
        console.warn('[Suggestions] Pre-load analysis token error:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [displaySuggestions, getAccessTokenSilently]);

  // ── Action a suggestion ─────────────────────────────────────────────

  const actionSuggestion = useCallback(
    async (suggestion: DisplaySuggestion) => {
      const idx = suggestion.originalIndex;

      // Already actioned — no-op
      if (actionedIndices.has(idx)) return;

      const type = suggestion.type || 'task';

      // Tasks open the AddTodoModal
      if (type === 'task') {
        setTodoModalSuggestion(suggestion);
        setTodoModalOpen(true);
        return;
      }

      // Projects and autopilots are actioned immediately
      setActioning(idx);
      try {
        const token = await getAccessTokenSilently();
        await api.post(
          '/onboarding/action-suggestion',
          { suggestionIndex: idx },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setActionedIndices((prev) => {
          const next = new Set(prev);
          next.add(idx);
          return next;
        });
      } catch (err) {
        console.error('[Suggestions] Failed to action suggestion:', err);
        setError('Something went wrong. Please try again.');
      } finally {
        setActioning(null);
      }
    },
    [actionedIndices, getAccessTokenSilently],
  );

  // ── Task creation callback (from AddTodoModal) ──────────────────────

  const handleTodoCreated = useCallback(
    async (todo: UserTodoItem) => {
      if (!todoModalSuggestion) return;
      const idx = todoModalSuggestion.originalIndex;

      // If the user changed the description in the modal, store the override
      if (
        todo?.description &&
        todo.description !== todoModalSuggestion.shortDescription
      ) {
        setOverriddenDescriptions((prev) => {
          const next = new Map(prev);
          next.set(idx, todo.description);
          return next;
        });
      }

      // Mark as actioned in backend (include override if text was changed)
      const descriptionOverride =
        todo?.description &&
        todo.description !== todoModalSuggestion.shortDescription
          ? todo.description
          : undefined;
      try {
        const token = await getAccessTokenSilently();
        await api.post(
          '/onboarding/action-suggestion',
          { suggestionIndex: idx, descriptionOverride },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (err) {
        console.error('[Suggestions] Failed to persist task action:', err);
      }

      setActionedIndices((prev) => {
        const next = new Set(prev);
        next.add(idx);
        return next;
      });
      setTodoModalOpen(false);
      setTodoModalSuggestion(null);
    },
    [todoModalSuggestion, getAccessTokenSilently],
  );

  const handleTodoModalClose = useCallback(() => {
    setTodoModalOpen(false);
    setTodoModalSuggestion(null);
  }, []);

  // ── Finish / Skip ───────────────────────────────────────────────────

  const completeSuggestions = useCallback(
    async (selectedIndices: number[], destination: OnboardingDestination) => {
      setSubmitting(true);
      setError(null);
      try {
        const token = await getAccessTokenSilently();
        await api.post<CompleteSuggestionsResponse>(
          '/onboarding/complete-suggestions',
          { selectedIndices },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        await onFinished(destination);
      } catch (err) {
        console.error('[Suggestions] Failed to complete suggestions:', err);
        setError('Something went wrong. Please try again.');
      } finally {
        setSubmitting(false);
      }
    },
    [getAccessTokenSilently, onFinished],
  );

  const handleFinish = useCallback(
    () => completeSuggestions(Array.from(actionedIndices), 'home'),
    [completeSuggestions, actionedIndices],
  );

  const handleSkip = useCallback(
    () => completeSuggestions([], 'home'),
    [completeSuggestions],
  );

  const handleInviteColleagues = useCallback(
    () => completeSuggestions(Array.from(actionedIndices), 'invite-colleagues'),
    [completeSuggestions, actionedIndices],
  );

  const hasActioned = actionedIndices.size > 0;
  const displayName = assistantNickname || assistantName || 'your secretary';

  // ── Render ──────────────────────────────────────────────────────────

  const renderSuggestionItem = (suggestion: DisplaySuggestion) => {
    const idx = suggestion.originalIndex;
    const isActioned = actionedIndices.has(idx);
    const isLoading = actioning === idx;
    const type = (suggestion.type || 'task') as SuggestionType;
    const config = TYPE_CONFIG[type];

    return (
      <button
        key={idx}
        className={`suggestion-item${isActioned ? ' actioned' : ''}`}
        onClick={() =>
          !isActioned && !isLoading && actionSuggestion(suggestion)
        }
        disabled={isActioned || isLoading}
      >
        <span
          className={`suggestion-action-btn${isActioned ? ' checked' : ''}`}
        >
          {isActioned ? (
            <CheckIcon />
          ) : isLoading ? (
            <span className='suggestion-spinner' />
          ) : (
            <PlusIcon />
          )}
        </span>
        <span className='suggestion-text'>
          {overriddenDescriptions.get(suggestion.originalIndex) ||
            suggestion.shortDescription}
          {isActioned && (
            <span className='suggestion-subtitle'>{config.subtitle}</span>
          )}
        </span>
      </button>
    );
  };

  const renderTypeGroup = (type: SuggestionType) => {
    const items = groupedSuggestions[type];
    if (items.length === 0) return null;
    const config = TYPE_CONFIG[type];

    return (
      <div key={type} className='suggestion-group'>
        <span className={`type-tag ${config.tagClass}`}>{config.label}</span>
        {items.map(renderSuggestionItem)}
      </div>
    );
  };

  return (
    <div className='onboarding-step suggestions-step'>
      <div className='suggestions-layout'>
        {/* ── Left panel ──────────────────────────────────────────── */}
        <div className='suggestions-left'>
          <h1 className='suggestions-title'>More ways to work</h1>
          <div className='suggestions-description'>
            <p>
              Sometimes you'll want {displayName} to work on bigger projects, or
              do things on autopilot.
            </p>
          </div>

          <div className='info-cards'>
            <div className='info-card info-card-task'>
              <span className='info-card-icon'>
                <OnboardingTaskIcon />
              </span>
              <span className='info-card-label'>Tasks</span>
              <span className='info-card-desc'>
                Buy, research, email and more. I'll get it done.
              </span>
            </div>
            <div className='info-card info-card-project'>
              <span className='info-card-icon'>
                <OnboardingProjectIcon />
              </span>
              <span className='info-card-label'>Projects</span>
              <span className='info-card-desc'>
                Plan bigger ideas. I'll coordinate a checklist as you go.
              </span>
            </div>
            <div className='info-card info-card-autopilot'>
              <span className='info-card-icon'>
                <OnboardingAutopilotIcon />
              </span>
              <span className='info-card-label'>Autopilots</span>
              <span className='info-card-desc'>
                Create scheduled or reactive tasks — fire and forget.
              </span>
            </div>
          </div>

          <p className='suggestions-footer-text'>
            When you're done choose <strong>'Finished'</strong> to open Dream
            and add even more.
          </p>

          <div className='suggestions-actions'>
            {hasActioned ? (
              <button
                className='pill-button primary'
                onClick={handleFinish}
                disabled={submitting}
              >
                {submitting ? 'Finishing...' : 'Finished'}
              </button>
            ) : (
              <button
                className='pill-button'
                onClick={handleSkip}
                disabled={submitting}
              >
                {submitting ? 'Finishing...' : 'Skip'}
              </button>
            )}
          </div>
          {error && <p className='suggestions-error'>{error}</p>}

          {organisationsEnabled === true && (
            <p className='suggestions-team-prompt'>
              Working with a team? Set up an organisation to invite colleagues,
              give them their own secretaries and pay for everyone on one bill.{' '}
              <button
                type='button'
                className='suggestions-team-link'
                onClick={handleInviteColleagues}
                disabled={submitting}
              >
                Invite colleagues
              </button>
            </p>
          )}
        </div>

        {/* ── Right panel ─────────────────────────────────────────── */}
        <div className='suggestions-right'>
          <p className='suggestions-right-header'>Choose + to add to my work</p>
          {generationStatus === 'pending' && suggestions.length === 0 ? (
            <div className='suggestions-loading'>
              <span className='suggestion-spinner large' />
              <p>Analysing your calendar and email…</p>
            </div>
          ) : (
            <div className='suggestion-groups'>
              {renderTypeGroup('task')}
              {renderTypeGroup('project')}
              {renderTypeGroup('autopilot')}
            </div>
          )}
        </div>
      </div>

      {/* ── AddTodoModal for task suggestions ─────────────────────── */}
      {todoModalOpen && todoModalSuggestion && (
        <div className='todo-modal-overlay'>
          <OnboardingModalsProvider>
            <AddTodoModal
              isOpen={todoModalOpen}
              onClose={handleTodoModalClose}
              agentName={assistantNickname || assistantName || undefined}
              agentImageUrl={assistantImageUrl || undefined}
              agentId={assistantId || undefined}
              onTodoCreated={handleTodoCreated}
              initialText={todoModalSuggestion.shortDescription}
              excludeActionKeys={ONBOARDING_EXCLUDE_ACTION_KEYS}
              preloadedAnalysis={
                analysisCache.current.get(todoModalSuggestion.originalIndex) ||
                null
              }
            />
          </OnboardingModalsProvider>
        </div>
      )}
    </div>
  );
};

export default SuggestionsStep;
